import * as React from "react";
import * as ReactDOM from "react-dom";
import * as moment from "moment";
import * as globals from "../../globals";
import * as enums from "../../enumerations";
import * as models from "../../models";
import * as modelsGet from "../../models/get";
import * as utils from "../../utils/utils";
import * as utilsCalendar from "../../utils/calendar";
import * as apiSettings from "../../api/settings";
import { Time } from "../../types/time";
import { Loader, LoaderStyle } from "../loader";
import { OptionalLink } from "../optionalLink";
import { MonthItem } from "./monthItem";
import { YearItem } from "./yearItem";
import { DayItem } from "./dayItem";
import { CalendarViewMode } from "../../enumerations";

enum DatepickerMode
{
    day,
    month,
    year
}

interface IProperties
{
    /**
     * Das momentan selektierte Datum.
     */
    selectedDate?: moment.Moment,

    /**
     * Die momentan selektierte Kalendaransicht.
     */
    selectedViewMode?: enums.CalendarViewMode,

    /**
     * Die Höhe des Datepickers.
     */
    height?: number,

    /**
     * Die Breite des Datepickers.
     */
    width?: number,

    /**
     * Anzahl der sichtbaren Datepicker.
     */
    count?: number,

    /**
     * Angabe, ob der selektierte Bereich anhand der momentanen Kalenderansicht, dargestellt werden soll.
     */
    isSelectedRangeVisible?: boolean,

    /**
     * Angabe, ob der Button zum hinzufügen von Datepickern, sichtbar ist.
     */
    isAddButtonVisible?: boolean,

    /**
     * Wird ausgeführt, sobald sich das Datum des Datepickers ändert.
     */
    onDateChanged?: (newDate: moment.Moment, type: "year" | "month" | "day") => void,

    /**
     * Wird ausgeführt, sobald ein Datepicker hinzugefügt wird.
     */
    onDatepickerAdded?: (newCount: number) => void,

    /**
     * Wird ausgeführt, sobald ein Datepicker entfernt wird.
     */
    onDatepickerRemove?: (newCount: number) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Das momentan selektierte Datum.
     */
    selectedDate?: moment.Moment,

    /**
     * Der momentane Modus des Datepickers.
     */
    mode?: DatepickerMode,

    /**
     * Enthält alle von der Komponente benötigten Einstellungen.
     */
    settings?: {
        hiddenDaysInWorkweek?: number[]
    }

    /**
     * Der momentane Anzahl der Datepicker.
     */
    count?: number
}

export class Datepicker extends React.Component<IProperties, IState>
{
    /**
     * Enthält die Kurzschreibweise aller Wochentage, sortiert.
     */
    private _weekdaysMin: string[];

    /**
     * Enthält alle Monate ausgeschrieben.
     */
    private _months: string[];

    /**
     * Standardwerte der Eigenschaften setzen.
     */
    public static defaultProps: Partial<IProperties> = {
        width: 250,
        height: 280,
        count: 1,
        isSelectedRangeVisible: false,
        isAddButtonVisible: false
    };

    /**
     * Erstellt eine neue Instanz der Klasse @see Datepicker.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        this._weekdaysMin = moment.weekdaysMin(true);
        this._months = moment.months();

        this.state = {
            selectedDate: props.selectedDate ? props.selectedDate : moment(),
            mode: DatepickerMode.day,
            settings: {
                hiddenDaysInWorkweek: [6, 7]
            },
            count: props.isAddButtonVisible ? props.count : 1
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        // Benötigte Einstellungen laden.
        var hiddenDaysInWorkweek = await globals.getSettingAsync<number[]>(globals.SettingKey.daysHiddenInWorkweek)

        // State setzen.
        this.setState({
            isLoading: false,
            settings: {
                hiddenDaysInWorkweek: hiddenDaysInWorkweek
            }
        });
    }

    /**
     * Ändert den Anzeigemodus des Datepickers. (Tage, Monate, Jahre)
     */
    private changeMode = (): void =>
    {
        if (this.state.mode === DatepickerMode.day)
        {
            this.setState({
                mode: DatepickerMode.month
            });
        }
        else if (this.state.mode === DatepickerMode.month)
        {
            this.setState({
                mode: DatepickerMode.year
            });
        }
        else
        {
            this.setState({
                mode: DatepickerMode.day
            });
        }
    }

    /**
     * Zum vorherigen Monat springen.
     */
    private gotoPreviousMonth = (): void =>
    {
        var date: moment.Moment = moment(this.state.selectedDate).subtract(1, "months");
        this.setState({
            selectedDate: date
        }, () => this.props.onDateChanged && this.props.onDateChanged(moment(date), "month"));
    }

    /**
     * Zum nächsten Monat springen.
     */
    private gotoNextMonth = (): void => 
    {
        var date: moment.Moment = moment(this.state.selectedDate).add(1, "months");
        this.setState({
            selectedDate: date
        }, () => this.props.onDateChanged && this.props.onDateChanged(moment(date), "month"));
    }

    /**
     * Zu einem beliebigen Monat springen.
     */
    private gotoMonth = (month: number, year: number): void => 
    {
        var date: moment.Moment = moment([year, month - 1, this.state.selectedDate.date()]);
        this.setState({
            selectedDate: date,
            mode: DatepickerMode.day
        }, () => this.props.onDateChanged && this.props.onDateChanged(moment(date), "month"));
    }

    /**
     * Zum vorherigen Jahr springen.
     */
    private gotoPreviousYear = (): void =>
    {
        var date: moment.Moment = moment(this.state.selectedDate).subtract(1, "years");
        this.setState({
            selectedDate: date
        });
    }

    /**
     * Zum nächsten Jahr springen.
     */
    private gotoNextYear = (): void => 
    {
        var date: moment.Moment = moment(this.state.selectedDate).add(1, "years");
        this.setState({
            selectedDate: date
        });
    }

    /**
     * Zum vorherigen Jahres Gruppe springen.
     */
    private gotoPreviousYearRange = (): void =>
    {
        var date: moment.Moment = moment(this.state.selectedDate).subtract(12, "years");
        this.setState({
            selectedDate: date
        });
    }

    /**
     * Zum nächsten Jahres Gruppe springen.
     */
    private gotoNextYearRange = (): void => 
    {
        var date: moment.Moment = moment(this.state.selectedDate).add(12, "years");
        this.setState({
            selectedDate: date
        });
    }

    /**
     * Zu einem beliebigen Jahr springen.
     */
    private gotoYear = (year: number): void => 
    {
        var date: moment.Moment = moment([year, this.state.selectedDate.month(), this.state.selectedDate.date()]);
        this.setState({
            selectedDate: date,
            mode: DatepickerMode.day
        }, () => this.props.onDateChanged && this.props.onDateChanged(moment(date), "year"));
    }

    /**
     * Zu einem beliebigen Tag springen.
     */
    private onDayClick = (newDate: moment.Moment): void =>
    {
        this.props.onDateChanged && this.props.onDateChanged(moment(newDate), "day")
    }

    /**
     * Fügt einen weiteren Datepicker zur Ansicht hinzu.
     */
    private addDatepicker = (): void =>
    {
        const count = this.state.count + 1;
        this.setState({
            count: count
        }, () => this.props.onDatepickerAdded && this.props.onDatepickerAdded(count));
    }

    /**
     * Entfernt einen Datepicker aus der Ansicht.
     */
    private removeDatepicker = (): void =>
    {
        const count = this.state.count - 1;
        this.setState({
            count: count
        }, () => this.props.onDatepickerRemove && this.props.onDatepickerRemove(count));
    }

    /**
     * Prüft ob das momentan angezeigte Datum (im Kalender) im Datepicker angezeigt wird.
     * @param date Das Datum das geprüft werden soll.
     */
    private isDateInSelectedRange(date: moment.Moment): boolean
    {
        const { selectedDate, selectedViewMode, isSelectedRangeVisible } = this.props;
        const { settings } = this.state;
        const currentDate = moment(date);
        var visibleDays: number[] = utilsCalendar.getVisibleDays();

        if (isSelectedRangeVisible && selectedViewMode != null)
        {
            switch (selectedViewMode)
            {
                case enums.CalendarViewMode.month:
                    {
                        return currentDate.isSame(selectedDate, "month");
                    }
                case enums.CalendarViewMode.week:
                    {
                        let rangeFrom: moment.Moment = moment(selectedDate).startOf("week");
                        let rangeTo: moment.Moment = moment(selectedDate).endOf("week");
                        if (currentDate.isBetween(rangeFrom, rangeTo, "day") || currentDate.isSame(rangeFrom, "day") || currentDate.isSame(rangeTo, "day"))
                            return true;
                    }
                    break;
                case enums.CalendarViewMode.workWeek:
                    {
                        visibleDays = utilsCalendar.getVisibleDays(settings.hiddenDaysInWorkweek);
                        let firstDay = visibleDays.first();
                        let lastDay = visibleDays[visibleDays.length - 1];
                        let rangeFrom: moment.Moment = moment(selectedDate).getStartOfWeek(firstDay);
                        let rangeTo: moment.Moment = moment(selectedDate).getEndOfWeek(lastDay);

                        if (currentDate.isBetween(rangeFrom, rangeTo, "day") || currentDate.isSame(rangeFrom, "day") || currentDate.isSame(rangeTo, "day"))
                            return true;
                    }
                    break;
                case enums.CalendarViewMode.day:
                case enums.CalendarViewMode.planning:
                    {
                        if (currentDate.isSame(selectedDate, "day"))
                            return true;
                    }
                    break;
            }
        }

        return false;
    };
    
    /**
     * Rendert die Header Komponente des Datepickers.
     */
    private renderHeaderComponent(): JSX.Element[]
    {
        var headers = [];
        headers.push(
            <div
                className="datepicker_calendar_header datepicker_calendar_header--kw"
                style={utils.getGridChildStyle(1, 1, 1, 1)}>
                KW
            </div>
        );

        var index = 2;
        for (var header of this._weekdaysMin)
        {
            headers.push(
                <div
                    className="datepicker_calendar_header"
                    style={utils.getGridChildStyle(index, index, 1, 1)}>
                    {header}
                </div>
            );
            index++;
        }

        return headers;
    }

    /**
     * Rendert die Toolbar Komponente des Datepickers.
     * @param date Das Datum welches für den Header Text verwendet werden soll.
     * @param hideArrows Angabe, ob die Navigation ausgeblendet werden soll.
     */
    private renderToolbarComponent(date: moment.Moment, hideArrows: boolean, isLast: boolean): JSX.Element
    {
        var dateFormat: string = "";
        var onPreviousClick: () => void;
        var onNextClick: () => void;
        switch (this.state.mode)
        {
            case DatepickerMode.day:
                {
                    dateFormat = date.format("MMMM YYYY");
                    onPreviousClick = this.gotoPreviousMonth;
                    onNextClick = this.gotoNextMonth;
                }
                break;
            case DatepickerMode.month:
                {
                    dateFormat = date.format("YYYY");
                    onPreviousClick = this.gotoPreviousYear;
                    onNextClick = this.gotoNextYear;
                }
                break;
            case DatepickerMode.year:
                {
                    dateFormat = moment(date).format("YYYY") + " - " + moment(date).add(11, "years").format("YYYY");
                    onPreviousClick = this.gotoPreviousYearRange;
                    onNextClick = this.gotoNextYearRange;
                }
                break;
        }

        return (
            <div className="datepicker_toolbar">
                {
                    !hideArrows && (
                        <div
                            className="datepicker_toolbar_item"
                            onClick={onPreviousClick}>
                            <i className="datepicker_toolbar_item_icon mdi mdi-chevron-left"></i>
                        </div>
                    )
                }
                <div
                    className="datepicker_toolbar_item datepicker_toolbar_item--fill"
                    onClick={this.changeMode}>
                    <span className="datepicker_toolbar_item_text">{dateFormat}</span>
                </div>
                {
                    !hideArrows && (
                        <div
                            className="datepicker_toolbar_item"
                            onClick={onNextClick}>
                            <i className="datepicker_toolbar_item_icon mdi mdi-chevron-right"></i>
                        </div>
                    )
                }
                {
                    this.props.isAddButtonVisible && (
                        <div
                            className="datepicker_toolbar_item"
                            onClick={isLast ? this.removeDatepicker : this.addDatepicker}>
                            <i className={`datepicker_toolbar_item_icon mdi mdi-${isLast ? "minus" : "plus"}`}></i>
                        </div>
                    )
                }
            </div>
        );
    }

    /**
     * Rendert die anzuzeigenen Tage.
     * @param date Das Datum anhand dessen die Tage ermittelt werden sollen.
     * @returns Eine Komponente mit den Tagen die im Datepicker angzeigt werden sollen.
     */
    private renderDaysComponent(date: moment.Moment): JSX.Element[]
    {
        var selectedDate: moment.Moment = moment(date);
        var nowWithoutTime: moment.Moment = moment().startOf("day");
        var daysInMonth: number = date.daysInMonth();
        var firstDayInMonth: number = moment(date).startOf("month").day();
        firstDayInMonth = (firstDayInMonth === 0) ? 6 : firstDayInMonth - 1;
        var previousMonth: number = moment(date).subtract(1, "months").month();
        var nextMonth: number = moment(date).add(1, "months").month();
        var yearOfPreviousMonth: number = moment(date).subtract(1, "months").year();
        var yearOfNextMonth: number = moment(date).add(1, "months").year();
        var daysInPreviousMonth: number = moment([yearOfPreviousMonth, previousMonth, 1]).daysInMonth();

        var daysInWeek: any[] = [];
        var weeks: any[] = [];
        for (var i = 0, r = 0; i < 42; i++)
        {
            var index = 1 + (i - firstDayInMonth);
            var day: moment.Moment;

            if (index <= 0)
            {
                day = moment([selectedDate.year(), selectedDate.month(), 1]).subtract(Math.abs(index - 1), "days");
            }
            else if (index > daysInMonth)
            {
                day = moment([selectedDate.year(), selectedDate.month(), daysInMonth]).add(index - daysInMonth, "days");
            }
            else
            {
                day = moment([selectedDate.year(), selectedDate.month(), index]);
            }

            var isToday: boolean = day.isSame(nowWithoutTime);
            var isOutsideCurrentMonth: boolean = i < firstDayInMonth || i >= (daysInMonth + firstDayInMonth);
            var isFirstDayOfMonth: boolean = day.isSame(moment([day.year(), day.month(), 1]));
            var dayNumber: number = 1 + (i - firstDayInMonth);
            var monthNumber: number = selectedDate.month();
            var yearNumber: number = selectedDate.year();

            if (isOutsideCurrentMonth)
            {
                if (i < firstDayInMonth)
                {
                    dayNumber = daysInPreviousMonth + dayNumber;
                    monthNumber = previousMonth;
                    yearNumber = yearOfPreviousMonth;
                }
                else
                {
                    dayNumber = dayNumber - daysInMonth;
                    monthNumber = nextMonth;
                    yearNumber = yearOfNextMonth;
                }
            }

            var currentDate: moment.Moment = moment([yearNumber, monthNumber, dayNumber]);
            var classNames: string[] = ["datepicker_calendar_day"];
            var isDateInSelectedRange: boolean = this.isDateInSelectedRange(currentDate);

            daysInWeek.push(
                <DayItem
                    style={utils.getGridChildStyle(r + 2, r + 2, weeks.length + 2, weeks.length + 2)}
                    text={currentDate.date().toString()}
                    date={moment(currentDate)}
                    isDateInSelectedRange={isDateInSelectedRange}
                    isOutsideCurrentMonth={isOutsideCurrentMonth}
                    isToday={isToday}
                    onClick={this.onDayClick} />
            );

            if (++r == 7)
            {
                daysInWeek.unshift(
                    <div
                        className="datepicker_calendar_day datepicker_calendar_day--kw"
                        style={utils.getGridChildStyle(1, 2, weeks.length + 2, weeks.length + 2)}>
                        {currentDate.isoWeek()}
                    </div>
                );
                weeks.push(
                    daysInWeek
                );

                r = 0;
                daysInWeek = [];
            }
        }

        return weeks;
    }

    /**
     * Rendert die Komponente.
     */
    public render(): JSX.Element
    {
        const { isLoading, selectedDate, mode, count } = this.state;

        if (isLoading)
        {
            return (
                <div className="datepicker-container">
                    <Loader loaderStyle={LoaderStyle.style1} />
                </div>
            );
        }

        // Alle Daten anhand der Anzahl der anzuzeigenden Datepicker erstellen.
        var dates: moment.Moment[] = [];
        for (var i = 0; i < count; i++)
        {
            if (mode === DatepickerMode.day)
                dates.push((i > 0) ? moment(selectedDate).add(i, "months") : moment(selectedDate));
            else if (mode === DatepickerMode.month)
                dates.push((i > 0) ? moment(selectedDate).add(i, "years") : moment(selectedDate));
            else if (mode === DatepickerMode.year)
                dates.push((i > 0) ? moment(selectedDate).add(12, "years") : moment(selectedDate));
        }

        // Datepicker anhand des momentanen Modus erstellen.
        var datepicker: JSX.Element[] = [];
        switch (mode)
        {
            case DatepickerMode.day:
                {
                    let headers = this.renderHeaderComponent();
                    for (let i = 0; i < dates.length; i++)
                    {
                        let toolbar = this.renderToolbarComponent(dates[i], (i > 0), (count > 1 && i === count - 1));
                        let weeks = this.renderDaysComponent(dates[i]);
                        datepicker.push(
                            <div
                                className="datepicker"
                                style={{
                                    height: undefined // this.props.height - 1
                                }}>
                                {toolbar}
                                <div className="datepicker_calendar">
                                    {headers}
                                    {weeks}
                                </div>
                            </div>
                        );
                    }
                }
                break;
            case DatepickerMode.month:
                {
                    for (let i = 0; i < dates.length; i++)
                    {
                        let toolbar = this.renderToolbarComponent(dates[i], (i > 0), (count > 1 && i === count - 1));
                        let column: number = 0;

                        datepicker.push(
                            <div
                                className="datepicker"
                                style={{
                                    height: undefined // this.props.height - 1
                                }}>
                                {toolbar}
                                <div className="datepicker_months">
                                    {
                                        this._months.map((month, monthIndex) =>
                                        {
                                            let index: number = monthIndex + 1;
                                            let row: number = 1

                                            if (index <= 3)
                                            {
                                                row = 1;
                                            }
                                            else if (index > 3 && index <= 6)
                                            {
                                                column = (column >= 3) ? 0 : column;
                                                row = 2;
                                            }
                                            else if (index > 6 && index <= 9)
                                            {
                                                column = (column >= 3) ? 0 : column;
                                                row = 3;
                                            }
                                            else
                                            {
                                                column = (column >= 3) ? 0 : column;
                                                row = 4
                                            }

                                            column++;

                                            return (
                                                <MonthItem
                                                    style={utils.getGridChildStyle(column, column, row, row)}
                                                    text={month}
                                                    month={monthIndex + 1}
                                                    year={dates[i].year()}
                                                    onClick={this.gotoMonth} />
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        );
                    }
                }
                break;
            case DatepickerMode.year:
                {
                    for (let i = 0; i < dates.length; i++)
                    {
                        let years: number[] = [];
                        let toolbar = this.renderToolbarComponent(dates[i], (i > 0), (count > 1 && i === count - 1));
                        let column: number = 0;

                        for (let year = dates[i].year(); year < dates[i].year() + 12; year++)
                        {
                            years.push(year);
                        }

                        datepicker.push(
                            <div
                                className="datepicker"
                                style={{
                                    height: undefined // this.props.height - 1
                                }}>
                                {toolbar}
                                <div className="datepicker_years">
                                    {
                                        years.map((y, yearIndex) =>
                                        {
                                            let index: number = yearIndex + 1;
                                            let row: number = 1

                                            if (index <= 3)
                                            {
                                                row = 1;
                                            }
                                            else if (index > 3 && index <= 6)
                                            {
                                                column = (column >= 3) ? 0 : column;
                                                row = 2;
                                            }
                                            else if (index > 6 && index <= 9)
                                            {
                                                column = (column >= 3) ? 0 : column;
                                                row = 3;
                                            }
                                            else
                                            {
                                                column = (column >= 3) ? 0 : column;
                                                row = 4
                                            }

                                            column++;

                                            return (
                                                <YearItem
                                                    style={utils.getGridChildStyle(column, column, row, row)}
                                                    text={y.toString()}
                                                    year={y}
                                                    onClick={this.gotoYear} />
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        );
                    }
                }
                break;
        }

        return (
            <div className="datepicker-container"
                style={{
                    height: undefined, //this.props.height * this.state.count,
                    width: undefined //this.props.width
                }}>
                {datepicker}
            </div>
        );
    }
}